"use client";

// Schedule store — per-brand schedules and per-batch post dates.
//
// A sixth store rather than a corner of ironside-script-store.ts, for the same
// reason it is its own store: `brand_schedule` is keyed by BRAND
// SLUG, and `week_date` by a batch (creatorEmail+brandSlug+week), neither of
// which is a script id. Same shape as the feedback store: module-level Maps, one
// version + listener channel, an applyServerRows that ignores foreign kinds, and
// persist() on every write.
//
// `week_date` is the first time a "batch" becomes a stored object with a
// property of its own — until now it was only a grouping of scripts on the
// Videos page. Two creators share a brand's schedule but each own
// their own Week 3 post date, so the date is keyed per creator+brand+week.
//
// `brand_commitment` (2026-09-11) is the fourth kind and the same shape one
// component shorter: how many videos a week THIS creator owes THIS brand, keyed
// `creator::brand`. It decides how many slots a new week is minted with and it
// is what Throughput and the brand record now count as committed.
//
// `brand_approval` (2026-09-11) is the fifth, and the shortest key of all: one
// brand slug, one seat. Who gives a brand's FINAL approval — the brand itself,
// or their AM. It is the same fact the other four are, a property of how this
// brand's work flows, written from the same page by the same holders.

import { persist, isConfirmed, forget, relocate } from "./store-persist";
import { sameData } from "./same-data";
import type { BrandSchedule } from "./schedule-dates";
import {
  COMMITMENT_KIND,
  commitmentKey,
  readCommitment,
  type Commitment,
} from "./commitment";
import {
  APPROVAL_KIND,
  DEFAULT_FINAL_APPROVER,
  readApproval,
  type BrandApproval,
} from "./brand-approval";
import type { FinalApprover } from "./stage-model";
import { readWeekAssignment, type WeekAssignmentSnapshot } from "./script-scheduling";

// The plan's TYPES live in lib/week-slots.ts, beside the one function that
// reads a plan into slots, so the server can import them without reaching into
// a client store. Re-exported here because every caller already asks this file.
import type { WeekPlan } from "./week-slots";
export type { DayPlan, WeekPlan } from "./week-slots";

const schedules = new Map<string, BrandSchedule>(); // brand SLUG -> schedule
const weekDates = new Map<string, string>(); // "creatorEmail::brandSlug::week" -> "YYYY-MM-DD"
const weekPlans = new Map<string, WeekPlan>(); // same key as weekDates -> plan
const commitments = new Map<string, Commitment>(); // "creatorEmail::brandSlug" -> videos a week
const approvals = new Map<string, BrandApproval>(); // brand SLUG -> who holds the last seat
const assignments = new Map<string, WeekAssignmentSnapshot>(); // read-only; editor group only
const listeners = new Set<() => void>();
let version = 0;

function notify() {
  version++;
  listeners.forEach((l) => l());
}

/**
 * The composite key for a batch's post date. Kept private so callers can't drift.
 *
 * TWO IDS AND A NUMBER — never a display name. This key is written into the db,
 * so a creator renaming their Slack profile or an admin renaming a brand would
 * strand every post date behind a key nothing looks up any more.
 */
const weekKey = (creatorEmail: string, brandSlug: string, week: number) =>
  `${creatorEmail}::${brandSlug}::${week}`;

export function getBrandSchedule(brandSlug: string): BrandSchedule | undefined {
  return schedules.get(brandSlug);
}

export function saveBrandSchedule(brandSlug: string, schedule: BrandSchedule): void {
  schedules.set(brandSlug, schedule);
  persist("brand_schedule", brandSlug, schedule); // keyed by SLUG
  notify();
}

export function getWeekDate(creatorEmail: string, brandSlug: string, week: number): string | undefined {
  return weekDates.get(weekKey(creatorEmail, brandSlug, week));
}

/** Ignore a stale snapshot while a date is being changed or removed. */
export function getWeekAssignment(creatorEmail: string, brandSlug: string, week: number): WeekAssignmentSnapshot | undefined {
  const key = weekKey(creatorEmail, brandSlug, week);
  const assignment = assignments.get(key);
  return assignment?.postDate === weekDates.get(key) ? assignment : undefined;
}

export function saveWeekDate(creatorEmail: string, brandSlug: string, week: number, iso: string, optimisticPlan?: WeekPlan): Promise<boolean> {
  const key = weekKey(creatorEmail, brandSlug, week);
  weekDates.set(key, iso);
  // The server shifts the saved plan in the date write's transaction. Sending
  // an already-shifted plan separately races that shift and can move days twice.
  if (optimisticPlan) weekPlans.set(key, optimisticPlan);
  const written = persist("week_date", key, iso, {
    confirmed: true,
    mirrors: optimisticPlan ? [{ kind: 'week_plan', id: key, data: optimisticPlan }] : undefined,
  });
  notify();
  return written;
}

/**
 * The stored plan for a batch, or undefined for a week nobody has edited. A
 * week with no plan is NOT planless: its plan is derived
 * from the scripts' `videoNum`s, with no skips (weekSlotsFrom in
 * ironside-script-store). A row appears here only the first time an AM saves an
 * edit, and never before.
 */
export function getWeekPlan(creatorEmail: string, brandSlug: string, week: number): WeekPlan | undefined {
  return weekPlans.get(weekKey(creatorEmail, brandSlug, week));
}

export function saveWeekPlan(creatorEmail: string, brandSlug: string, week: number, plan: WeekPlan): void {
  const key = weekKey(creatorEmail, brandSlug, week);
  weekPlans.set(key, plan);
  persist("week_plan", key, plan);
  notify();
}

/**
 * Re-key a week's date and plan from one composite key to another — the shared
 * half of the two operations that change part of that key: a week moving brand
 * (moveWeekSchedule) and a week changing number (renumberWeekSchedule).
 *
 * ONE FUNCTION, BECAUSE THE HALF-DONE FAILURE IS THE SAME BUG BOTH WAYS. Leaving
 * the old row behind is the trap: a week created later under the vacated key —
 * same brand, or same number — would find dates waiting for it and adopt them
 * silently (the Week-18 shape). And every row here has to go together, because a
 * week whose date moved and whose plan did not renders as five videos in no
 * order under a date that belongs to something else.
 *
 * A MISSING SOURCE ROW DELETES THE DESTINATION'S rather than leaving it alone.
 * The destination may already hold a stale row from a week that was itself moved
 * or abandoned; if the week arriving has no date, it must not silently inherit
 * that one. Absent means absent, in both directions.
 *
 * THE DURABLE HALF IS `relocate`, NOT A WRITE AND A DELETE SIDE BY SIDE. This
 * used to fire both at once, so a server that refused the write while accepting
 * the delete destroyed the row: written nowhere, deleted from the one place it
 * lived. See relocate's own note. The in-memory move stays immediate either way
 * — the Maps are authoritative for render, and the notice is what says the
 * server disagrees.
 */
function rekeyWeek(from: string, to: string): void {
  const move = <T,>(map: Map<string, T>, kind: string) => {
    const value = map.get(from);
    if (value === undefined) {
      if (!map.has(to)) return;
      map.delete(to);
      forget(kind, to);
      return;
    }
    map.set(to, value);
    map.delete(from);
    relocate(kind, from, to, value);
  };

  move(weekDates, "week_date");
  move(weekPlans, "week_plan");
  notify();
}

/**
 * Carry a week's dates and plan from one brand to another — the schedule half of
 * the retired brand move (lib/week-reassign.ts, 2026-09-02).
 *
 * THE KEY CONTAINS THE BRAND, so this is a move, not an edit: every row is
 * written under the new key and deleted under the old one. See rekeyWeek.
 */
export function moveWeekSchedule(
  creatorEmail: string,
  fromBrand: string,
  toBrand: string,
  week: number,
): void {
  if (fromBrand === toBrand) return;
  rekeyWeek(weekKey(creatorEmail, fromBrand, week), weekKey(creatorEmail, toBrand, week));
}

/**
 * Carry a week's dates and plan to a new NUMBER — the schedule half of an AM
 * renumbering a week (lib/week-renumber.ts).
 *
 * THE KEY CONTAINS THE NUMBER, which is the whole reason this exists and is not
 * simply a field write on five scripts. Renumbering week 4 to week 6 and leaving
 * the date under `::4` gives the renamed week no post date and no plan, while
 * arming `::4` for whatever takes that number next — the same two failures
 * moveWeekSchedule was written for, one component of the key along.
 *
 * The caller checks for a collision first (weekRenumberBlocker); this would
 * happily overwrite the destination's rows, which is exactly why nothing may
 * call it without asking.
 */
export function renumberWeekSchedule(
  creatorEmail: string,
  brandSlug: string,
  fromWeek: number,
  toWeek: number,
): void {
  if (fromWeek === toWeek) return;
  rekeyWeek(weekKey(creatorEmail, brandSlug, fromWeek), weekKey(creatorEmail, brandSlug, toWeek));
}

// ─── The commitment: how many videos a week this creator owes this brand ────
//
// A FOURTH KIND IN THIS STORE, not a fifth store, because it is the same fact
// the other three are: a brand's cadence. The schedule says WHEN a batch's steps
// fall, the post date says when one batch goes out, and this says HOW BIG a
// batch is — all three keyed on the same two ids, all three written from the
// same AM's pages.
//
// ⚠️ UNSET IS A REAL STATE AND IS NOT ZERO. Absent means nobody has said, and
// every reader falls back to the app's own five; zero means somebody deliberately
// said this creator owes nothing. Which is why clearing is a TOMBSTONE (a null
// row) rather than a number.

/** This creator's commitment to this brand, or undefined when nobody has set one. */
export function getCommitment(creatorEmail: string, brandSlug: string): Commitment | undefined {
  return commitments.get(commitmentKey(creatorEmail, brandSlug));
}

/**
 * Set it — or clear it back to the default with `null`.
 *
 * The VALUE is the caller's to validate (lib/commitment.ts's `commitmentValue`
 * throws the sentence to show them); what this owes is that a cleared control
 * and an unset one are the same state on every client, which is what the
 * tombstone buys. /api/store's DELETE allowlist holds `week_date` and
 * `week_plan` only — deliberately, they are the two whose KEY can be inherited
 * by a later week — so "back to no value" is expressed the way the scripts
 * express it, by writing a null row.
 */
export function saveCommitment(
  creatorEmail: string,
  brandSlug: string,
  videosPerWeek: number | null,
  setBy: string,
): void {
  const key = commitmentKey(creatorEmail, brandSlug);
  // ⚠️ THE KIND IS SPELLED OUT HERE RATHER THAN PASSED AS `COMMITMENT_KIND`,
  // and it is not carelessness. tests/unit/store-kind-route.test.ts finds the
  // app's writable kinds by sweeping persist() call sites for a LITERAL first
  // argument, and a kind that only ever reaches persist through a variable is
  // invisible to it — which is exactly the sweep that makes an ungated kind fail
  // on the commit that adds it rather than on the day somebody uses it. The
  // constant and this literal are pinned to each other in
  // tests/unit/commitment.test.ts.
  if (videosPerWeek === null) {
    commitments.delete(key);
    persist("brand_commitment", key, null);
  } else {
    const row: Commitment = { videosPerWeek, setBy };
    commitments.set(key, row);
    persist("brand_commitment", key, row);
  }
  notify();
}

// ─── The approval seat: who gives this brand's final approval ───────────────
//
// A FIFTH KIND IN THIS STORE, and the same argument the commitment made for
// being the fourth: it is a property of how a brand's work flows, keyed on the
// brand, written from the AM's Schedule page. The schedule says WHEN each step
// falls, the commitment says HOW BIG a batch is, and this says WHO SIGNS IT OFF.
//
// ⚠️ UNSET IS A REAL STATE AND IT MEANS "brand" — the two-seat ladder every
// brand has run on since the app existed. Clearing is therefore a TOMBSTONE (a
// null row), the way a cleared commitment is, so that "back to the usual
// arrangement" is a state every client agrees about rather than a row one of
// them is still holding.

/** The stored seat for a brand, or undefined when nobody has chosen one. */
export function getBrandApproval(brandSlug: string): BrandApproval | undefined {
  return approvals.get(brandSlug);
}

/**
 * WHO HOLDS THIS BRAND'S LAST SEAT — always an answer, never undefined.
 *
 * The one every caller wants: the stage model needs a seat on every move, and a
 * brand nobody has configured has one, it is just not written down. The raw row
 * above is for the CONTROL, which must be able to tell "nobody has said" from
 * "somebody chose the default" — a page that cannot is a page whose radio button
 * claims a decision was made.
 *
 * An unreadable row falls back too (readApproval returns null), and the fallback
 * is the safe misreading: asking for an approval that may not be needed, rather
 * than granting one nobody gave.
 */
export function finalApproverFor(brandSlug: string): FinalApprover {
  return approvals.get(brandSlug)?.finalApprover ?? DEFAULT_FINAL_APPROVER;
}

/**
 * Choose the seat — or clear it back to the default with `null`.
 *
 * ⚠️ THE KIND IS SPELLED OUT HERE RATHER THAN PASSED AS `APPROVAL_KIND`, for the
 * reason saveCommitment gives at length: tests/unit/store-kind-route.test.ts
 * finds the app's writable kinds by sweeping persist() call sites for a LITERAL
 * first argument, and a kind that only ever reaches persist through a variable
 * is invisible to it. The constant and this literal are pinned to each other in
 * tests/unit/brand-approval.test.ts.
 */
export function saveBrandApproval(
  brandSlug: string,
  finalApprover: FinalApprover | null,
  setBy: string,
): void {
  if (finalApprover === null) {
    approvals.delete(brandSlug);
    persist("brand_approval", brandSlug, null);
  } else {
    const row: BrandApproval = { finalApprover, setBy };
    approvals.set(brandSlug, row);
    persist("brand_approval", brandSlug, row);
  }
  notify();
}

export function subscribeSchedule(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getScheduleVersion(): number {
  return version;
}

// ─── Hydration from the server ───────────────────────────────────────────────
// Every kind is Map-backed: upsert by key, and a key deleted on the server (the
// diff's `deleted` — Delete week's date and plan) leaves its
// map. Skip keys with an unconfirmed local write (the pending-write guard), bump version only when something moved — a
// row equal to what the map holds (lib/same-data.ts) and a tombstone for a key
// already gone are not moves, so an idle poll notifies nobody — and never
// persist() from here — that would echo server writes back at the server.
export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  for (const d of deleted) {
    const map = heldByKind(d.kind);
    if (!map || !isConfirmed(d.kind, d.id, null)) continue;
    if (map.delete(d.id)) changed = true;
  }
  for (const r of rows) {
    const mine =
      r.kind === "brand_schedule" ||
      r.kind === "week_date" ||
      r.kind === "week_plan" ||
      r.kind === "week_assignment" ||
      r.kind === COMMITMENT_KIND ||
      r.kind === APPROVAL_KIND;
    if (!mine) continue; // foreign kind
    if (!isConfirmed(r.kind, r.id, r.data)) continue;
    if (r.kind === "brand_schedule") { if (upsert(schedules, r.id, r.data as BrandSchedule)) changed = true; }
    else if (r.kind === "week_date") { if (upsert(weekDates, r.id, r.data as string)) changed = true; }
    else if (r.kind === "week_plan") { if (upsert(weekPlans, r.id, r.data as WeekPlan)) changed = true; }
    else if (r.kind === "week_assignment") {
      const row = readWeekAssignment(r.data);
      if (row ? upsert(assignments, r.id, row) : assignments.delete(r.id)) changed = true;
    } else if (r.kind === APPROVAL_KIND) {
      // A TOMBSTONE KIND TOO, and the same branch shape for the same reason: a
      // cleared seat arrives as `data: null` and must REMOVE the row, so that
      // "nobody has chosen" reads the same here as it does on the client that
      // cleared it. An unreadable payload is treated as absent, which is the
      // two-seat default — see readApproval.
      const row = readApproval(r.data);
      if (row ? upsert(approvals, r.id, row) : approvals.delete(r.id)) changed = true;
    } else {
      // ⚠️ A KIND WHOSE ROW CAN BE A TOMBSTONE, and it is why this
      // branch reads the payload rather than casting it. A cleared commitment
      // arrives as `data: null` and must REMOVE the number — an upsert of null
      // would leave a row nothing can read, which `readCommitment` would then
      // treat as absent anyway, but only after the map had stopped agreeing
      // with the server about what it holds.
      const row = readCommitment(r.data);
      if (row ? upsert(commitments, r.id, row) : commitments.delete(r.id)) changed = true;
    }
  }
  if (changed) notify();
}

/** The map a kind of this store's is held in, or undefined for a foreign kind. */
function heldByKind(kind: string): Map<string, unknown> | undefined {
  if (kind === "brand_schedule") return schedules;
  if (kind === "week_date") return weekDates;
  if (kind === "week_plan") return weekPlans;
  if (kind === "week_assignment") return assignments;
  if (kind === COMMITMENT_KIND) return commitments;
  if (kind === APPROVAL_KIND) return approvals;
  return undefined;
}

/** Set `id` to the server's value unless the map already holds the same thing. True when it moved. */
function upsert<T>(map: Map<string, T>, id: string, value: T): boolean {
  if (map.has(id) && sameData(map.get(id), value)) return false;
  map.set(id, value);
  return true;
}
